/**
 * Notifications page for broadcast notification management
 * 
 * Sub-tabs:
 * - Compose: Create and send notification campaigns
 * - History: View past campaigns and delivery stats
 * 
 * Note: Lists/Audience management has been moved to its own section
 */

import React, { useImperativeHandle, forwardRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Send, History } from 'lucide-react';
import { CampaignComposer, CampaignHistory } from '../components/notifications';
import { CampaignDetailView } from '../components/notifications/CampaignDetailView';

export type BroadcastTab = 'compose' | 'history';

export const BROADCAST_TABS: { key: BroadcastTab; label: string; icon: React.ReactNode; createLabel?: string }[] = [
  { key: 'compose', label: 'Compose', icon: <Send className="w-4 h-4" /> },
  { key: 'history', label: 'History', icon: <History className="w-4 h-4" /> },
];

export interface NotificationsHandle {
  openCreate: () => void;
}

interface NotificationsProps {
  showPreviewModal?: boolean;
  setShowPreviewModal?: (show: boolean) => void;
}

// Helper to get current view info for breadcrumbs
export interface BroadcastViewInfo {
  isDetailView: boolean;
  title: string;
  parentTab: BroadcastTab;
  itemName?: string;
}

export function getBroadcastViewInfo(searchParams: URLSearchParams): BroadcastViewInfo {
  const tab = (searchParams.get('tab') as BroadcastTab) || 'compose';
  const campaignId = searchParams.get('campaignId');

  if (tab === 'history' && campaignId) {
    return {
      isDetailView: true,
      title: 'Campaign Details',
      parentTab: 'history',
    };
  }

  return {
    isDetailView: false,
    title: BROADCAST_TABS.find(t => t.key === tab)?.label || 'Broadcast',
    parentTab: tab,
  };
}

export const Notifications = forwardRef<NotificationsHandle, NotificationsProps>(({ showPreviewModal, setShowPreviewModal }, ref) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeTab = (searchParams.get('tab') as BroadcastTab) || 'compose';
  const campaignId = searchParams.get('campaignId');
  const viewInfo = getBroadcastViewInfo(searchParams);

  const setActiveTab = (tab: BroadcastTab) => {
    setSearchParams({ tab });
  };

  const handleCampaignSent = () => {
    // Could switch to history tab after sending, but the composer shows success state
  };
  
  // Expose the openCreate method to parent (no longer used for lists)
  useImperativeHandle(ref, () => ({
    openCreate: () => {
      // No-op - lists are now in Audience section
    }
  }));

  // Hide tab navigation when in detail view
  const showTabNav = !viewInfo.isDetailView;

  return (
    <div className="h-full flex flex-col">
      {/* Tab Navigation - Hidden in detail views */}
      {showTabNav && (
        <div className="border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary">
        <nav className="flex gap-1 px-6 pt-4" aria-label="Tabs">
            {BROADCAST_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`
                flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors
                ${
                  activeTab === tab.key
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-b-2 border-blue-600 -mb-px'
                      : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
                }
              `}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      )}

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-dark-bg-primary">
        {activeTab === 'compose' && <CampaignComposer onCampaignSent={handleCampaignSent} />}
        {activeTab === 'history' && (
          campaignId ? (
            <CampaignDetailView 
              campaignId={campaignId} 
              onBack={() => navigate('/dashboard/broadcast?tab=history')}
              showPreviewModal={showPreviewModal}
              setShowPreviewModal={setShowPreviewModal}
            />
          ) : (
            <CampaignHistory />
          )
        )}
      </div>
    </div>
  );
});

Notifications.displayName = 'Notifications';

export default Notifications;
